import React, { useEffect, useMemo, useRef, useState } from 'react';


const ProjectLoadingOverlay = ({
  isDarkMode = true,
  message = 'Restoring your workspace',
  substep = null,
  progress = null,
  showProgressBar = true,
}) => {
  // Unique id so multiple instances don't clash on SVG gradient ids
  const uid = useMemo(() => 'plo-' + Math.random().toString(36).slice(2, 8), []);
 
  // Strip trailing ellipses from message — we render our own animated dots+caret
  const cleanMsg = useMemo(
    () => String(message || '').replace(/[.…]+\s*$/, ''),
    [message]
  );
 
  // For indeterminate mode, optionally cycle a default substep so the user
  // always feels like *something* is happening.
  const [autoSubstep, setAutoSubstep] = useState(null);
  useEffect(() => {
    if (substep != null || progress != null) return;
    const stages = [
      'Reading project state…',
      'Reconciling local cache…',
      'Hydrating dashboard…',
    ];
    let i = 0;
    setAutoSubstep(stages[0]);
    const t = setInterval(() => {
      i = (i + 1) % stages.length;
      setAutoSubstep(stages[i]);
    }, 2200);
    return () => clearInterval(t);
  }, [substep, progress]);
 
  const effectiveSubstep = substep != null
    ? substep
    : (progress != null ? null : autoSubstep);
 
  // Theme tokens
  const isDark = !!isDarkMode;
  const tokens = isDark
    ? {
        bg:        '#0A0B0D',
        bgGrad:    'radial-gradient(ellipse 80% 60% at 50% 50%, #11141A 0%, #0A0B0D 60%, #07080A 100%)',
        msg:       '#B8BDC9',
        muted:     '#6B7180',
        dim:       '#3A3F4A',
        track:     'rgba(255,255,255,0.05)',
        trackBg:   'rgba(255,255,255,0.05)',
      }
    : {
        bg:        '#F5F6F8',
        bgGrad:    'radial-gradient(ellipse 80% 60% at 50% 50%, #FFFFFF 0%, #F2F4F7 60%, #E9ECF1 100%)',
        msg:       '#3A3F4A',
        muted:     '#6B7180',
        dim:       '#A8AEBA',
        track:     'rgba(0,0,0,0.06)',
        trackBg:   'rgba(0,0,0,0.06)',
      };
 
  const pct = progress == null ? null : Math.max(0, Math.min(100, Number(progress)));
 
  // Per-character message reveal
  const chars = useMemo(() => [...cleanMsg], [cleanMsg]);
 
  return (
    <div
      className={`plo-root ${uid}`}
      role="status" aria-live="polite" aria-label={message}
      style={{
        // Inline fallback for first-paint and any case where the
        // component-scoped <style> block fails to apply (production CSS
        // loading edge cases). These match the defaults in the <style>
        // block below; the style block can still override colors and
        // animation but layout is guaranteed.
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: '18px',
        background: tokens.bg,
        fontFamily: "'Outfit', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
        color: tokens.msg,
        overflow: 'hidden',
      }}
    >
      {/* Message block */}
      <div className="plo-msg-block">
        <div className="plo-msg-text" aria-hidden="true">
          {chars.map((c, i) => (
            <span
              key={i}
              className="plo-char"
              style={{ animationDelay: `${0.1 + i * 0.025}s` }}
            >
              {c === ' ' ? '\u00A0' : c}
            </span>
          ))}
          <span
            className="plo-char"
            style={{ animationDelay: `${0.1 + chars.length * 0.025}s` }}
          >
            …
          </span>
          <span className="plo-caret" />
        </div>
 
        {effectiveSubstep != null && (
          <div className="plo-substep">
            <span className="plo-substep-pulse" />
            <span className="plo-substep-text">{effectiveSubstep}</span>
          </div>
        )}
 
        {showProgressBar && (
          pct != null ? (
            <div className="plo-progress-row">
              <div className="plo-bar"><div className="plo-bar-fill" style={{ width: pct + '%' }} /></div>
              <div className="plo-bar-pct">{Math.floor(pct)}%</div>
            </div>
          ) : (
            <div className="plo-scan-track" />
          )
        )}
      </div>
 
      {/* Component-scoped styles. Class names are namespaced with `.${uid}`
          to avoid bleeding into / conflicting with the host app. */}
      <style>{`
        .plo-root.${uid} {
          position: fixed; inset: 0; z-index: 9999;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 18px;
          background: ${tokens.bg};
          background: ${tokens.bgGrad};
          font-family: 'Outfit', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
          -webkit-font-smoothing: antialiased;
          color: ${tokens.msg};
          overflow: hidden;
          isolation: isolate;
        }
 
        /* ─── message ───────────────────────────────────────────────── */
        .plo-root.${uid} .plo-msg-block {
          position: relative;
          z-index: 2;
          display: flex; flex-direction: column; align-items: center;
          gap: 12px;
          min-height: 60px;
        }
        .plo-root.${uid} .plo-msg-text {
          font-size: 13.5px;
          font-weight: 400;
          letter-spacing: 0.5px;
          color: ${tokens.msg};
          display: inline-flex; align-items: center; gap: 1px;
        }
        .plo-root.${uid} .plo-char {
          display: inline-block;
          opacity: 0;
          transform: translateY(4px);
          animation: plo-${uid}-char 0.4s ease-out forwards;
        }
        @keyframes plo-${uid}-char { to { opacity: 1; transform: translateY(0); } }
        .plo-root.${uid} .plo-caret {
          display: inline-block;
          width: 1px; height: 12px;
          background: #F5B547;
          margin-left: 4px;
          animation: plo-${uid}-caret 1s steps(2) infinite;
        }
        @keyframes plo-${uid}-caret { 50% { opacity: 0; } }
 
        .plo-root.${uid} .plo-substep {
          font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 10.5px;
          letter-spacing: 1.1px;
          text-transform: uppercase;
          color: ${tokens.dim};
          display: flex; align-items: center; gap: 8px;
          height: 14px;
        }
        .plo-root.${uid} .plo-substep-pulse {
          width: 5px; height: 5px; border-radius: 999px;
          background: #38B6FF;
          box-shadow: 0 0 6px #38B6FF;
          animation: plo-${uid}-substep 1.4s ease-in-out infinite;
        }
        @keyframes plo-${uid}-substep {
          0%, 100% { opacity: 0.4; transform: scale(0.85); }
          50%      { opacity: 1;   transform: scale(1); }
        }
 
        /* ─── progress thread ───────────────────────────────────────── */
        .plo-root.${uid} .plo-scan-track {
          position: relative;
          width: 180px; height: 1px;
          background: ${tokens.track};
          overflow: hidden;
          margin-top: 2px;
        }
        .plo-root.${uid} .plo-scan-track::after {
          content: '';
          position: absolute; top: 0; left: 0;
          width: 50%; height: 100%;
          background: linear-gradient(90deg, transparent 0%, #38B6FF 35%, #F5B547 65%, transparent 100%);
          animation: plo-${uid}-scan 1.8s cubic-bezier(.45,0,.55,1) infinite;
        }
        @keyframes plo-${uid}-scan {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
 
        .plo-root.${uid} .plo-progress-row {
          display: inline-flex; align-items: center; gap: 10px;
          margin-top: 2px;
        }
        .plo-root.${uid} .plo-bar {
          position: relative;
          width: 200px; height: 2px;
          background: ${tokens.trackBg};
          border-radius: 999px;
          overflow: hidden;
        }
        .plo-root.${uid} .plo-bar-fill {
          position: absolute; top: 0; left: 0; bottom: 0;
          width: 0%;
          background: linear-gradient(90deg, #38B6FF 0%, #F5B547 100%);
          border-radius: 999px;
          transition: width .5s cubic-bezier(.25,.8,.3,1);
          box-shadow: 0 0 8px rgba(56,182,255,0.5);
        }
        .plo-root.${uid} .plo-bar-pct {
          font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 10.5px;
          color: ${tokens.muted};
          min-width: 38px;
          text-align: right;
          font-variant-numeric: tabular-nums;
        }
 
        /* ─── reduced motion ────────────────────────────────────────── */
        @media (prefers-reduced-motion: reduce) {
          .plo-root.${uid} .plo-char,
          .plo-root.${uid} .plo-caret,
          .plo-root.${uid} .plo-substep-pulse,
          .plo-root.${uid} .plo-scan-track::after {
            animation: none !important;
          }
          .plo-root.${uid} .plo-char {
            opacity: 1 !important; transform: none !important;
          }
        }
      `}</style>
    </div>
  );
};
 
export default ProjectLoadingOverlay;